import React, { useEffect, useState, useRef } from "react";
import { useSelector } from "react-redux";
import DataTable from "react-data-table-component";
import { customStyles } from "../../styles/dataTableStyles";
import { api } from "../../api";
import { CSVLink } from "react-csv";

function DevcoProductsTable() {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isCollapsed, setIsCollapsed] = useState(false);
  const token = useSelector((state) => state.auth.token);

  const [downloadData, setDownloadData] = useState([]);
  const [isDownloading, setIsDownloading] = useState(false);
  const downloadRef = useRef();

  

  useEffect(() => {
    const fetchDevcoProducts = async () => {
      setLoading(true);
      setError(null);

      try {
        const response = await api.devco.getDevcoProducts({}, token);

        if (response?.code === 200 && response.data?.products) {
          setProducts(response.data.products);
        } else {
          setError("Failed to load Devco products");
        }
      } catch (error) {
        console.error("Error fetching Devco products:", error);
        setError("Failed to load Devco products");
      } finally {
        setLoading(false);
      }
    };

    if (token) {
      fetchDevcoProducts();
    }
  }, [token]);


    const prepareDevcoCsvData = (values = []) => {
    const headers = [
      "OEM ID",
      "OEM Name",
      "Brand ID",
      "Brand Name",
      "Format ID",
      "Format Description",
      "Format Fields",
      "Product ID",
      "Product Name",
      "SKU",
      "Base Currency",
      "Currency Symbol",
      "Unit Price",
      "Sell Price",
      "Available Stock",
      "Redemption Instructions",
      "Image URL",
    ];

    const csvArray = [headers];

    values.forEach((item) => {
       let redeemInstructions = item.OEM_PRODUCT_RedemptionInstructions ?? "NA";
       if (redeemInstructions !== "NA") {
          redeemInstructions = redeemInstructions
            .replace(/\r?\n/g, " ")       // remove new line breaks
            .replace(/"/g, '""');         // escape internal quotes
        }
        redeemInstructions = `"${redeemInstructions}"`;

      csvArray.push([
        item.OEM_ID ?? "NA",
        item.OEM_Name ?? "NA",
        item.OEM_BRAND_ID ?? "NA",
        item.OEM_BRAND_Name ?? "NA",
        item.OEM_BRAND_Brand_Product_Format_ID ?? "NA",
        item.OEM_BRAND_Brand_Product_Format_Desc ?? "NA",
        item.OEM_BRAND_Brand_Product_Format_Fields ?? "NA",
        item.OEM_PRODUCT_ID ?? "NA",
        item.OEM_PRODUCT_Name ?? "NA",
        item.OEM_PRODUCT_VVSSKU ?? "NA",
        item.OEM_PRODUCT_BaseCurrency ?? "NA",
        item.OEM_PRODUCT_BaseCurrencySymbol ?? "NA",
        item.OEM_PRODUCT_UnitPrice ?? "NA",
        item.OEM_PRODUCT_SellPrice ?? "NA",
        item.OEM_PRODUCT_Available ?? "NA",
        `"${redeemInstructions}"`,
        item.OEM_PRODUCT_ImageURL ?? "NA",
      ]);
    });

    return csvArray;
  };

    const handleDownload = () => {
    setIsDownloading(true);

    const csv = prepareDevcoCsvData(products);
    setDownloadData(csv);

    setTimeout(() => {
      downloadRef.current?.link?.click();
      setIsDownloading(false);
    }, 150);
  };



  // DataTable columns configuration
  const columns = [
    {
      name: "Product ID",
      selector: (row) => row.OEM_PRODUCT_ID,
      sortable: true,
    },
    {
      name: "Product",
      selector: (row) => row.OEM_PRODUCT_Name,
      cell: (row) => (
        <div>
          <strong title={row.OEM_PRODUCT_Name}>{row.OEM_PRODUCT_Name}</strong>
          <br />
          <small className="text-muted">{row.OEM_Name}</small>
        </div>
      ),
      sortable: true,
    },
    {
      name: "Unit Price",
      selector: (row) => row.OEM_PRODUCT_UnitPrice,
      cell: (row) => (
        <span>
          {row.OEM_PRODUCT_BaseCurrencySymbol} {row.OEM_PRODUCT_UnitPrice}
        </span>
      ),
      sortable: true,
    },
    {
      name: "Sell Price",
      selector: (row) => row.OEM_PRODUCT_SellPrice,
      cell: (row) => <strong>{row.OEM_PRODUCT_SellPrice}</strong>,
      sortable: true,
    },
    {
      name: "Stock",
      selector: (row) => row.OEM_PRODUCT_Available,
      cell: (row) => <span>{row.OEM_PRODUCT_Available}</span>,
      sortable: true,
    },
  ];

  return (
    <div className="row mt-4">
      <div className="col-sm-12">
        <div className="card">
          <div className="card-header d-flex justify-content-between align-items-center">
            <h5 className="card-title mb-0">
              <strong>Devco Products ({products.length})</strong>
            </h5>
            <button
              onClick={() => setIsCollapsed(!isCollapsed)}
              aria-expanded={!isCollapsed}
              style={{
                textDecoration: "none",
                outline: "none",
                border: "none",
                background: "#5C2E91",
                borderRadius: "4px",
              }}
            >
              <i
                className={`bx ${
                  isCollapsed ? "bx-chevron-down" : "bx-chevron-up"
                }`}
                style={{ color: "white" }}
              ></i>
            </button>
          </div>
          {!isCollapsed && (
            <div className="card-body">
              <div className="d-flex justify-content-end align-items-center mb-3 flex-wrap gap-2">
                <div className="d-flex align-items-center">
                    <button
                onClick={handleDownload}
                disabled={isDownloading}
                className="btn btn-primary ml-2 px-4">
                {isDownloading ? "Preparing..." : "Download"}
              </button>
              </div>
              </div>

              {/* Error Display */}
              {error && (
                <div className="alert alert-danger" role="alert">
                  {error}
                </div>
              )}

              {/* DataTable */}
              <div className="card card-table">
                <div>
                  <div className="reports">
                    <DataTable
                      columns={columns}
                      data={Array.isArray(products) ? products : []}
                      progressPending={loading}
                      highlightOnHover
                      pointerOnHover
                      customStyles={customStyles}
                      responsive
                      noHeader={false}
                      noTableHead={false}
                      wrap
                      noDataComponent={
                        <div className="text-center text-muted py-4">
                          <p>No products available.</p>
                        </div>
                      }
                    />
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

        <CSVLink
              data={downloadData}
              filename="devco_products.csv"
              className="d-none"
              ref={downloadRef}
              target="_blank"
        />
    </div>
  );
}

export default DevcoProductsTable;
